import * as XLSX from "xlsx-js-style";

const LEGUP_EXPORT_TYPE = "LEG_UP_PUBLIC_SIMULATION";
const LEGUP_SCHEMA_VERSION = "1.2";

/* =========================================================
   THEME
   ========================================================= */

const COLORS = {
  dark: "273A31",
  dark2: "344B3E",
  green: "8BB93C",
  greenDark: "5F862D",
  greenSoft: "EAF4DE",
  greenVerySoft: "F4F9EE",
  resultSoft: "F1F5EF",
  border: "D8E4D2",
  text: "243029",
  muted: "66736C",
  white: "FFFFFF",
  warning: "FFF4DF",
  warningText: "7A521A",
};

const FONT_NAME = "Lexend Deca";

const thinBorder = {
  top: { style: "thin", color: { rgb: COLORS.border } },
  bottom: { style: "thin", color: { rgb: COLORS.border } },
  left: { style: "thin", color: { rgb: COLORS.border } },
  right: { style: "thin", color: { rgb: COLORS.border } },
};

const styles = {
  title: {
    fill: { patternType: "solid", fgColor: { rgb: COLORS.dark } },
    font: { name: FONT_NAME, sz: 20, bold: true, color: { rgb: COLORS.white } },
    alignment: { vertical: "center", horizontal: "left" },
  },
  subtitle: {
    fill: { patternType: "solid", fgColor: { rgb: COLORS.dark } },
    font: { name: FONT_NAME, sz: 12, color: { rgb: "DFE9E1" } },
    alignment: { vertical: "center", horizontal: "left", wrapText: true },
  },
  section: {
    fill: { patternType: "solid", fgColor: { rgb: COLORS.dark2 } },
    font: { name: FONT_NAME, sz: 12, bold: true, color: { rgb: COLORS.white } },
    alignment: { vertical: "center", horizontal: "left" },
  },
  header: {
    fill: { patternType: "solid", fgColor: { rgb: "DDEBCF" } },
    font: { name: FONT_NAME, sz: 11, bold: true, color: { rgb: COLORS.dark } },
    border: thinBorder,
    alignment: { vertical: "center", horizontal: "left", wrapText: true },
  },
  body: {
    font: { name: FONT_NAME, sz: 11, color: { rgb: COLORS.text } },
    border: thinBorder,
    alignment: { vertical: "center", horizontal: "left", wrapText: true },
  },
  editable: {
    fill: { patternType: "solid", fgColor: { rgb: COLORS.greenVerySoft } },
    font: { name: FONT_NAME, sz: 11, bold: true, color: { rgb: COLORS.dark } },
    border: thinBorder,
    alignment: { vertical: "center", horizontal: "right", wrapText: true },
  },
  resultValue: {
    fill: { patternType: "solid", fgColor: { rgb: COLORS.resultSoft } },
    font: { name: FONT_NAME, sz: 11, bold: true, color: { rgb: COLORS.dark } },
    border: thinBorder,
    alignment: { vertical: "center", horizontal: "right", wrapText: true },
  },
  note: {
    fill: { patternType: "solid", fgColor: { rgb: COLORS.greenVerySoft } },
    font: { name: FONT_NAME, sz: 10, color: { rgb: COLORS.muted } },
    border: thinBorder,
    alignment: { vertical: "top", horizontal: "left", wrapText: true },
  },
  warning: {
    fill: { patternType: "solid", fgColor: { rgb: COLORS.warning } },
    font: {
      name: FONT_NAME,
      sz: 10,
      color: { rgb: COLORS.warningText },
      italic: true,
    },
    border: thinBorder,
    alignment: { vertical: "center", horizontal: "left", wrapText: true },
  },
  kpiLabel: {
    fill: { patternType: "solid", fgColor: { rgb: COLORS.greenSoft } },
    font: { name: FONT_NAME, sz: 10, bold: true, color: { rgb: COLORS.greenDark } },
    border: thinBorder,
    alignment: { vertical: "center", horizontal: "center", wrapText: true },
  },
  kpiValue: {
    fill: { patternType: "solid", fgColor: { rgb: COLORS.white } },
    font: { name: FONT_NAME, sz: 16, bold: true, color: { rgb: COLORS.dark } },
    border: thinBorder,
    alignment: { vertical: "center", horizontal: "center", wrapText: true },
  },
};

/* =========================================================
   GENERIC HELPERS
   ========================================================= */

function createExportId() {
  if (
    typeof window !== "undefined" &&
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
  ) {
    return window.crypto.randomUUID();
  }

  return `simulation-${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;
}

function cloneStyle(style) {
  return JSON.parse(JSON.stringify(style));
}

function applyStyleToRange(worksheet, rangeRef, style) {
  const range = XLSX.utils.decode_range(rangeRef);

  for (let row = range.s.r; row <= range.e.r; row += 1) {
    for (let col = range.s.c; col <= range.e.c; col += 1) {
      const address = XLSX.utils.encode_cell({ r: row, c: col });

      if (!worksheet[address]) {
        worksheet[address] = { t: "s", v: "" };
      }

      worksheet[address].s = cloneStyle(style);
    }
  }
}

function setCellStyle(
  worksheet,
  address,
  style,
  numberFormat = null,
) {
  if (!worksheet[address]) {
    return;
  }

  worksheet[address].s = cloneStyle(style);

  if (numberFormat) {
    worksheet[address].s.numFmt = numberFormat;
    worksheet[address].z = numberFormat;
  }
}

function safeExcelString(value) {
  const text = String(value ?? "");

  if (/^[=+\-@]/.test(text)) {
    return `'${text}`;
  }

  return text;
}

function normaliseValue(path, value) {
  if (value === null || value === undefined) {
    return "";
  }

  if (
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === "string") {
    const trimmed = value.trim();

    const protectedText =
      /(?:^|\.)(?:region|frequency|buildingType|propertyType|currentLabel|targetLabel|currentRating|rating|code|id)$/i
        .test(path) ||
      /postal|postcode/i.test(path);

    if (
      !protectedText &&
      /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/.test(trimmed)
    ) {
      const parsed = Number(trimmed);

      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    return safeExcelString(trimmed);
  }

  return safeExcelString(JSON.stringify(value));
}

function titleCase(value) {
  return String(value || "")
    .replace(/\[(\d+)\]/g, " $1 ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_\-.]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function finalToken(path) {
  const tokens = String(path || "")
    .replace(/\[\d+\]/g, "")
    .split(".")
    .filter(Boolean);

  return tokens[tokens.length - 1] || "";
}

function moneyFormat() {
  return '€ #,##0.00;[Red]-€ #,##0.00';
}

function percentFormat(value) {
  return Math.abs(Number(value) || 0) <= 1
    ? "0.00%"
    : '0.00"%"';
}

function numberFormatFor(path, value, explicitUnit = "") {
  if (typeof value !== "number") {
    return "@";
  }

  if (explicitUnit === "EUR") {
    return moneyFormat();
  }

  if (explicitUnit === "%") {
    return percentFormat(value);
  }

  if (
    explicitUnit === "years" ||
    explicitUnit === "months" ||
    explicitUnit === "m²"
  ) {
    return "#,##0";
  }

  const token = finalToken(path);

  const exactPercentFields = new Set([
    "monthlyInterestRate",
    "annualInterestRate",
    "taeg",
    "tae",
    "ltv",
    "ratio",
    "initialRate",
    "reviewRate",
    "purchaseTaxRate",
    "saving",
  ]);

  if (exactPercentFields.has(token)) {
    return percentFormat(value);
  }

  return "#,##0.00";
}

function displayValue(value, unit = "") {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (typeof value !== "number") {
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    return String(value);
  }

  if (unit === "EUR") {
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  if (unit === "%") {
    const pct =
      Math.abs(value) <= 1
        ? value * 100
        : value;

    return `${pct.toLocaleString("en", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}%`;
  }

  if (
    unit === "years" ||
    unit === "months"
  ) {
    return `${value.toLocaleString("en", {
      maximumFractionDigits: 0,
    })} ${unit}`;
  }

  if (unit) {
    return `${value.toLocaleString("en", {
      maximumFractionDigits: 2,
    })} ${unit}`;
  }

  return value.toLocaleString("en", {
    maximumFractionDigits: 2,
  });
}

function makeResultRow({
  key,
  section,
  label,
  value,
  unit = "",
  explanation,
}) {
  return {
    key,
    section,
    label,
    value: normaliseValue(key, value),
    unit,
    explanation,
  };
}

function pushIfValue(rows, config) {
  if (
    config.value === undefined ||
    config.value === null
  ) {
    return;
  }

  rows.push(makeResultRow(config));
}

function simulationTitle(simulationType) {
  const map = {
    LOAN_CAPACITY: "Loan capacity simulation",
    MONTHLY_INSTALLMENT: "Monthly instalment simulation",
    RENOVATION_COST: "Renovation cost simulation",
  };

  return map[simulationType] || titleCase(simulationType);
}

function regionLabel(region) {
  const map = {
    FLANDERS: "Flanders",
    GIRONA: "Girona",
  };

  return map[region] || titleCase(region);
}

/* =========================================================
   INPUTS
   ========================================================= */

function flattenInputs(
  value,
  prefix = "",
  rows = [],
) {
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      flattenInputs(
        item,
        `${prefix}[${index}]`,
        rows,
      );
    });

    return rows;
  }

  if (
    value &&
    typeof value === "object" &&
    !(value instanceof Date)
  ) {
    Object.entries(value).forEach(
      ([key, item]) => {
        const path = prefix
          ? `${prefix}.${key}`
          : key;

        flattenInputs(
          item,
          path,
          rows,
        );
      },
    );

    return rows;
  }

  rows.push({
    path: prefix || "value",
    value: normaliseValue(prefix, value),
  });

  return rows;
}

function inputLabel(path) {
  const insuranceMatch =
    /^insurances\.(lifeBorrower|lifeCoBorrower|fire)\.(premium|frequency|durationYears)$/
      .exec(path);

  if (insuranceMatch) {
    const [, insurance, field] =
      insuranceMatch;

    const insuranceLabels = {
      lifeBorrower:
        "Life insurance of borrower",
      lifeCoBorrower:
        "Life insurance of co-borrower",
      fire:
        "Fire insurance",
    };

    const fieldLabels = {
      premium: "Premium",
      frequency: "Payment frequency",
      durationYears: "Duration",
    };

    return `${insuranceLabels[insurance]} — ${fieldLabels[field]}`;
  }

  const map = {
    instalment:
      "Monthly instalment you wish to pay",
    loanAmount:
      "Loan amount",
    propertyPrice:
      "Property price",
    termYears:
      "Loan term",
    region:
      "Region of the property",
    borrowerCount:
      "Number of borrowers",
    oldestAge:
      "Age of oldest borrower",
    residence:
      "Residence / occupancy type",
    propertyType:
      "Property type",
    floorArea:
      "Floor area",
    buildingType:
      "Building type",
    storeys:
      "Number of storeys",
    currentLabel:
      "Current EPC label",
    targetLabel:
      "Target EPC label",
    currentRating:
      "Current energy rating",
    currentConsumption:
      "Current energy consumption",
    measures:
      "Selected renovation measures",
    nonEnergyWorks:
      "Selected non-energy works",
  };

  return map[path] || titleCase(finalToken(path));
}

function inputUnit(path) {
  if (
    path === "instalment" ||
    path === "loanAmount" ||
    path === "propertyPrice" ||
    /\.premium$/.test(path)
  ) {
    return "EUR";
  }

  if (
    path === "termYears" ||
    /\.durationYears$/.test(path)
  ) {
    return "years";
  }

  if (path === "floorArea") {
    return "m²";
  }

  if (path === "currentConsumption") {
    return "kWh/m²/year";
  }

  return "";
}

function inputExplanation(path) {
  if (path === "instalment") {
    return (
      "The monthly amount the Homebuyer is willing or able to pay. " +
      "For the Flanders loan-capacity simulator, this is the main affordability input."
    );
  }

  if (path === "termYears") {
    return (
      "The assumed repayment duration. A longer term normally reduces the monthly payment " +
      "but increases the total amount paid over the lifetime of the loan."
    );
  }

  if (path === "region") {
    return (
      "The pilot region used to select the applicable LEG-UP calculator assumptions and fees."
    );
  }

  if (/\.premium$/.test(path)) {
    return (
      "Insurance premium entered by the Homebuyer. When applicable, this cost is included " +
      "in the APRC and total loan-cost calculation."
    );
  }

  if (/\.frequency$/.test(path)) {
    return (
      "How often the insurance premium is paid. This determines how the premium contributes " +
      "to the total periodic cost."
    );
  }

  if (/\.durationYears$/.test(path)) {
    return (
      "How many years this insurance premium is assumed to be paid. This is a duration, not a percentage."
    );
  }

  return (
    "Homebuyer-provided input used by the simulator. This green cell may be edited. " +
    "After editing, upload the workbook back to LEG-UP to recalculate the results."
  );
}

/* =========================================================
   CURATED RESULTS
   ========================================================= */

function flandersLoanRows(result) {
  const rows = [];

  pushIfValue(rows, {
    key: "borrowingCapacity",
    section: "Main result",
    label: "Borrowing capacity",
    value: result?.loanAmount,
    unit: "EUR",
    explanation:
      "The maximum indicative loan principal that can be supported by the selected monthly instalment, term and calculator assumptions. This is not a lender approval.",
  });

  pushIfValue(rows, {
    key: "instalment",
    section: "Main result",
    label: "Monthly instalment",
    value: result?.instalment,
    unit: "EUR",
    explanation:
      "The monthly loan repayment used by the calculation. It should correspond to the monthly instalment entered in the Inputs sheet.",
  });

  pushIfValue(rows, {
    key: "taeg",
    section: "Rates",
    label: "APRC (TAEG / JKP)",
    value: result?.taeg,
    unit: "%",
    explanation:
      "The annual percentage rate of charge. It combines the loan interest rate with applicable mandatory or included costs so the overall borrowing cost can be compared more easily.",
  });

  pushIfValue(rows, {
    key: "monthlyInterestRate",
    section: "Rates",
    label: "Monthly interest rate",
    value: result?.monthlyInterestRate,
    unit: "%",
    explanation:
      "The monthly nominal interest rate applied to the outstanding loan balance.",
  });

  pushIfValue(rows, {
    key: "annualInterestRate",
    section: "Rates",
    label: "Equivalent annual interest rate",
    value: result?.annualInterestRate,
    unit: "%",
    explanation:
      "The annualised equivalent of the monthly interest rate. This is an interest-rate measure and is different from the APRC, which also reflects relevant costs.",
  });

  pushIfValue(rows, {
    key: "termYears",
    section: "Loan terms",
    label: "Loan term",
    value: result?.termYears,
    unit: "years",
    explanation:
      "The assumed number of years over which the loan is repaid.",
  });

  pushIfValue(rows, {
    key: "termMonths",
    section: "Loan terms",
    label: "Loan term in months",
    value: result?.termMonths,
    unit: "months",
    explanation:
      "The total number of monthly repayment periods used by the amortisation calculation.",
  });

  pushIfValue(rows, {
    key: "registrationAmount",
    section: "Mortgage registration",
    label: "Mortgage registration amount",
    value: result?.registrationAmount,
    unit: "EUR",
    explanation:
      "The amount used as the basis for mortgage-registration related duties and fees. It may be higher than the net loan principal because accessories are included.",
  });

  const notary = result?.notary || {};

  [
    ["registrationDuty", "Registration duty", notary.registrationDuty,
      "Duty calculated on the mortgage registration base."],
    ["mortgageDuty", "Mortgage duty", notary.mortgageDuty,
      "Mortgage-related duty included in the notarial costs."],
    ["retribution", "Notary retribution", notary.retribution,
      "Estimated notary remuneration used by the simulator."],
    ["administrativeFee", "Administrative fee", notary.administrativeFee,
      "Estimated administrative cost associated with the mortgage deed."],
    ["writingFee", "Writing fee", notary.writingFee,
      "Estimated deed-writing cost."],
    ["vat", "VAT on notary services", notary.vat,
      "Estimated VAT applied to the relevant notarial service components."],
    ["annexFee", "Annex fee", notary.annexFee,
      "Estimated fee for annexes or supporting deed documentation."],
    ["total", "Total notary fees", notary.total,
      "Total of the notarial fee components shown above."],
  ].forEach(([key, label, value, explanation]) => {
    pushIfValue(rows, {
      key: `notary.${key}`,
      section: "One-off costs",
      label,
      value,
      unit: "EUR",
      explanation,
    });
  });

  [
    ["regionalContribution", "Regional contribution", result?.regionalContribution,
      "Estimated regional contribution included in the one-off transaction costs."],
    ["valuationFee", "Valuation fee", result?.valuationFee,
      "Estimated cost of the property valuation."],
    ["fileFee", "File fee", result?.fileFee,
      "Estimated file or dossier administration fee."],
    ["extraCosts", "Other one-off costs", result?.extraCosts,
      "Any additional one-off cost included by the calculator."],
    ["oneOffCosts", "Total one-off costs", result?.oneOffCosts,
      "Total one-off costs associated with setting up the loan, excluding periodic insurance costs."],
  ].forEach(([key, label, value, explanation]) => {
    if (key === "extraCosts" && !value) {
      return;
    }

    pushIfValue(rows, {
      key,
      section: "One-off costs",
      label,
      value,
      unit: "EUR",
      explanation,
    });
  });

  (result?.periodicItems || []).forEach((item) => {
    const labelMap = {
      lifeBorrower:
        "Life insurance of borrower",
      lifeCoBorrower:
        "Life insurance of co-borrower",
      fire:
        "Fire insurance",
    };

    const friendly =
      labelMap[item.key] ||
      titleCase(item.key);

    pushIfValue(rows, {
      key: `periodic.${item.key}.total`,
      section: "Periodic costs",
      label: `${friendly} — total`,
      value: item.total,
      unit: "EUR",
      explanation:
        `${friendly}: ${displayValue(item.premium, "EUR")} paid ${titleCase(item.frequency).toLowerCase()} for ${item.durationYears} years. The displayed value is the total premium included over the assumed duration.`,
    });
  });

  pushIfValue(rows, {
    key: "periodicCosts",
    section: "Periodic costs",
    label: "Total periodic costs",
    value: result?.periodicCosts,
    unit: "EUR",
    explanation:
      "Total insurance and other recurring costs included over their assumed payment durations.",
  });

  pushIfValue(rows, {
    key: "costsAtSigning",
    section: "Overall cost",
    label: "Costs payable at signing",
    value: result?.costsAtSigning,
    unit: "EUR",
    explanation:
      "Indicative amount of costs assumed to be payable when the loan is signed.",
  });

  pushIfValue(rows, {
    key: "oneDayInterest",
    section: "Overall cost",
    label: "One-day interest",
    value: result?.oneDayInterest,
    unit: "EUR",
    explanation:
      "Interest cost for one day at the simulated loan amount and rate, used where required by the cost methodology.",
  });

  pushIfValue(rows, {
    key: "totalInterest",
    section: "Overall cost",
    label: "Total loan interest",
    value: result?.totalInterest,
    unit: "EUR",
    explanation:
      "Estimated interest paid over the full repayment term, excluding the original loan principal and non-interest fees.",
  });

  pushIfValue(rows, {
    key: "totalCosts",
    section: "Overall cost",
    label: "Total costs",
    value: result?.totalCosts,
    unit: "EUR",
    explanation:
      "Estimated non-principal costs included by the calculator, such as interest, one-off fees and eligible periodic costs.",
  });

  pushIfValue(rows, {
    key: "totalPayable",
    section: "Overall cost",
    label: "Total amount payable",
    value: result?.totalPayable,
    unit: "EUR",
    explanation:
      "Indicative total cash outflow over the simulation horizon: principal plus the costs included by the calculator.",
  });

  return rows;
}

function gironaLoanRows(result) {
  const rows = [];

  if (!result || result.error) {
    return rows;
  }

  pushIfValue(rows, {
    key: result.withPrice
      ? "loanAmount"
      : "propertyPrice",
    section: "Main result",
    label: result.withPrice
      ? "Indicative loan amount"
      : "Maximum indicative property price",
    value: result.withPrice
      ? result.loanAmount
      : result.propertyPrice,
    unit: "EUR",
    explanation: result.withPrice
      ? "Indicative mortgage amount calculated for the entered property price and borrower information."
      : "Indicative maximum property price supported by the borrower information and calculator assumptions.",
  });

  [
    ["savings", "Savings / own funds needed", result.savings, "EUR",
      "Indicative amount of own funds required in addition to the loan."],
    ["instalmentInitial", "Initial monthly instalment", result.instalmentInitial, "EUR",
      "Indicative monthly instalment during the initial interest-rate period."],
    ["instalmentReview", "Monthly instalment after rate review", result.instalmentReview, "EUR",
      "Indicative monthly instalment after the interest-rate review, where applicable."],
    ["propertyPrice", "Property price", result.propertyPrice, "EUR",
      "Property price used by the simulation."],
    ["loanAmount", "Loan amount", result.loanAmount, "EUR",
      "Indicative principal amount financed by the loan."],
    ["ltv", "Loan-to-value (LTV)", result.ltv, "%",
      "Loan amount divided by property price. A higher LTV means a larger share of the property value is financed."],
    ["initialRate", "Initial nominal interest rate (TIN)", result.initialRate, "%",
      "Nominal interest rate applied during the initial period."],
    ["reviewRate", "Interest rate after review", result.reviewRate, "%",
      "Indicative nominal interest rate after the review period, where applicable."],
    ["tae", "APR (TAE)", result.tae, "%",
      "Annual percentage rate used to express the overall annualised borrowing cost."],
    ["ratio", "Income affordability ratio", result.ratio, "%",
      "Share of relevant borrower income consumed by the simulated loan repayment under the calculator assumptions."],
    ["termYears", "Loan term", result.termYears, "years",
      "Number of years assumed for repayment."],
    ["termMonths", "Loan term in months", result.termMonths, "months",
      "Total number of monthly repayment periods."],
  ].forEach(([key, label, value, unit, explanation]) => {
    pushIfValue(rows, {
      key,
      section: key.includes("Rate") || ["ltv", "tae", "ratio"].includes(key)
        ? "Rates and affordability"
        : "Loan terms",
      label,
      value,
      unit,
      explanation,
    });
  });

  const breakdown = result.breakdown || {};

  [
    ["appraisal", "Property appraisal", breakdown.appraisal,
      "Estimated cost of the property valuation."],
    ["purchaseTax", "Purchase tax", breakdown.purchaseTax,
      "Estimated property-purchase tax according to the selected Girona/Catalonia assumptions."],
    ["notary", "Notary cost", breakdown.notary,
      "Estimated notarial cost associated with the purchase and financing transaction."],
    ["commission", "Bank commission", breakdown.commission,
      "Estimated bank commission included by the calculator."],
    ["taxesAndExpenses", "Total taxes and expenses", result.taxesAndExpenses,
      "Total of the taxes and transaction expenses estimated by the calculator."],
  ].forEach(([key, label, value, explanation]) => {
    pushIfValue(rows, {
      key: `breakdown.${key}`,
      section: "Taxes and expenses",
      label,
      value,
      unit: "EUR",
      explanation,
    });
  });

  return rows;
}

function flandersInstallmentRows(result) {
  if (!result) {
    return [];
  }

  return [
    makeResultRow({
      key: "instalment",
      section: "Main result",
      label: "Estimated monthly instalment",
      value: result.instalment,
      unit: "EUR",
      explanation:
        "Indicative monthly repayment for the entered loan amount and term.",
    }),
    makeResultRow({
      key: "loanAmount",
      section: "Loan terms",
      label: "Loan amount",
      value: result.loanAmount,
      unit: "EUR",
      explanation:
        "Loan principal entered by the Homebuyer.",
    }),
    makeResultRow({
      key: "termYears",
      section: "Loan terms",
      label: "Loan term",
      value: result.termYears,
      unit: "years",
      explanation:
        "Assumed repayment duration in years.",
    }),
    makeResultRow({
      key: "termMonths",
      section: "Loan terms",
      label: "Loan term in months",
      value: result.termMonths,
      unit: "months",
      explanation:
        "Total number of monthly repayment periods.",
    }),
    makeResultRow({
      key: "monthlyInterestRate",
      section: "Rates",
      label: "Monthly interest rate",
      value: result.monthlyInterestRate,
      unit: "%",
      explanation:
        "Monthly nominal interest rate used by the instalment calculation.",
    }),
    makeResultRow({
      key: "annualInterestRate",
      section: "Rates",
      label: "Equivalent annual interest rate",
      value: result.annualInterestRate,
      unit: "%",
      explanation:
        "Annualised equivalent of the monthly interest rate used by the simulation.",
    }),
    makeResultRow({
      key: "totalRepaid",
      section: "Overall cost",
      label: "Total amount repaid",
      value: result.totalRepaid,
      unit: "EUR",
      explanation:
        "Total of all scheduled loan repayments over the selected term.",
    }),
    makeResultRow({
      key: "totalInterest",
      section: "Overall cost",
      label: "Total interest paid",
      value: result.totalInterest,
      unit: "EUR",
      explanation:
        "Difference between total repayments and the original loan principal.",
    }),
  ];
}

function gironaInstallmentRows(result) {
  if (!result) {
    return [];
  }

  const rows = [
    makeResultRow({
      key: "loanAmount",
      section: "Loan terms",
      label: "Loan amount",
      value: result.loanAmount,
      unit: "EUR",
      explanation:
        "Loan principal used to compare the available mortgage product structures.",
    }),
    makeResultRow({
      key: "propertyPrice",
      section: "Loan terms",
      label: "Property price",
      value: result.propertyPrice,
      unit: "EUR",
      explanation:
        "Property value used in the loan-to-value calculation.",
    }),
    makeResultRow({
      key: "ltv",
      section: "Loan terms",
      label: "Loan-to-value (LTV)",
      value: result.ltv,
      unit: "%",
      explanation:
        "Loan amount divided by property price.",
    }),
    makeResultRow({
      key: "termYears",
      section: "Loan terms",
      label: "Loan term",
      value: result.termYears,
      unit: "years",
      explanation:
        "Selected repayment duration.",
    }),
  ];

  Object.entries(result.products || {}).forEach(
    ([productKey, product]) => {
      if (!product) {
        return;
      }

      const productName =
        titleCase(productKey);

      [
        ["instalmentInitial", "Initial monthly instalment", "EUR",
          "Monthly repayment during the initial period of this product."],
        ["instalmentReview", "Monthly instalment after review", "EUR",
          "Monthly repayment after the interest-rate review, where applicable."],
        ["initialRate", "Initial nominal interest rate", "%",
          "Nominal interest rate applied during the initial product period."],
        ["reviewRate", "Interest rate after review", "%",
          "Nominal interest rate after the review period, where applicable."],
        ["tae", "APR (TAE)", "%",
          "Annual percentage rate expressing the overall annualised borrowing cost for this product."],
        ["totalRepaid", "Total amount repaid", "EUR",
          "Total repayments over the selected term for this product."],
        ["totalInterest", "Total interest paid", "EUR",
          "Estimated total interest paid over the selected term for this product."],
      ].forEach(
        ([field, label, unit, explanation]) => {
          pushIfValue(rows, {
            key: `products.${productKey}.${field}`,
            section: `${productName} product`,
            label: `${productName} — ${label}`,
            value: product[field],
            unit,
            explanation,
          });
        },
      );
    },
  );

  return rows;
}

function flandersRenovationRows(results) {
  const rows = [];
  const budget = results?.budget;
  const perMeasure = results?.perMeasure;

  if (budget) {
    [
      ["budget.low", "Estimated renovation budget — low", budget.budget?.low, "EUR",
        "Lower end of the indicative budget range for reaching the selected target EPC label."],
      ["budget.typical", "Estimated renovation budget — typical", budget.budget?.typical, "EUR",
        "Central indicative renovation budget for reaching the selected target EPC label."],
      ["budget.high", "Estimated renovation budget — high", budget.budget?.high, "EUR",
        "Upper end of the indicative budget range for reaching the selected target EPC label."],
      ["currentValue", `Current EPC (${budget.currentLabel || "current"})`, budget.currentValue, "kWh/m²/year",
        "Reference energy-performance value associated with the current EPC label."],
      ["targetValue", `Target EPC (${budget.targetLabel || "target"})`, budget.targetValue, "kWh/m²/year",
        "Reference energy-performance value associated with the selected target EPC label."],
      ["epcImprovement", "Indicative EPC improvement", budget.epcImprovement, "kWh/m²/year",
        "Difference between the current and target reference energy-performance values."],
    ].forEach(([key, label, value, unit, explanation]) => {
      pushIfValue(rows, {
        key: `budget.${key}`,
        section: "Target-label budget",
        label,
        value,
        unit,
        explanation,
      });
    });
  }

  if (perMeasure) {
    [
      ["total.low", "Selected measures — low total", perMeasure.total?.low],
      ["total.typical", "Selected measures — typical total", perMeasure.total?.typical],
      ["total.high", "Selected measures — high total", perMeasure.total?.high],
      ["energyTotal.typical", "Energy measures — typical total", perMeasure.energyTotal?.typical],
      ["nonEnergyTotal.typical", "Non-energy works — typical total", perMeasure.nonEnergyTotal?.typical],
    ].forEach(([key, label, value]) => {
      pushIfValue(rows, {
        key: `perMeasure.${key}`,
        section: "Selected measures",
        label,
        value,
        unit: "EUR",
        explanation:
          "Indicative cost derived from the selected renovation measures and the Flanders reference cost data.",
      });
    });

    [
      ...(perMeasure.energy || []).map((row) => ({
        ...row,
        section: "Energy measures",
      })),
      ...(perMeasure.nonEnergy || []).map((row) => ({
        ...row,
        section: "Non-energy works",
      })),
    ].forEach((row) => {
      pushIfValue(rows, {
        key: `measure.${row.measure}.typical`,
        section: row.section,
        label: `${titleCase(row.measure)} — typical cost`,
        value: row.typical,
        unit: "EUR",
        explanation:
          `Indicative range ${displayValue(row.low, "EUR")} to ${displayValue(row.high, "EUR")} for this selected measure.`,
      });
    });
  }

  return rows;
}

function gironaRenovationRows(result) {
  if (!result) {
    return [];
  }

  const rows = [];

  [
    ["budget", "Indicative renovation budget", result.budget, "EUR",
      "Indicative façade-renovation budget produced by the Girona cost model."],
    ["currentRating", "Current energy rating", result.currentRating, "",
      "Current EPC/energy rating used as the baseline for the estimate."],
    ["facadeArea", "Estimated façade area", result.facadeArea, "m²",
      "Estimated façade surface used to calculate the renovation budget."],
    ["costPerM2", "Indicative cost per m²", result.costPerM2, "EUR",
      "Reference renovation cost per square metre used by the calculator."],
    ["consumption", "Current energy consumption", result.consumption, "kWh/m²/year",
      "Baseline annual energy consumption used for the projected scenarios."],
  ].forEach(([key, label, value, unit, explanation]) => {
    pushIfValue(rows, {
      key,
      section: "Current estimate",
      label,
      value,
      unit,
      explanation,
    });
  });

  ["conservative", "standard"].forEach((scenario) => {
    const projected =
      result.projected?.[scenario];

    if (!projected) {
      return;
    }

    const name = titleCase(scenario);

    [
      ["saving", `${name} scenario — energy saving`, projected.saving, "%",
        "Indicative reduction in energy consumption relative to the current baseline."],
      ["consumption", `${name} scenario — projected consumption`, projected.consumption, "kWh/m²/year",
        "Projected annual energy consumption after the works under this scenario."],
      ["rating", `${name} scenario — projected rating`, projected.rating, "",
        "Projected EPC/energy rating after the works under this scenario."],
    ].forEach(([field, label, value, unit, explanation]) => {
      pushIfValue(rows, {
        key: `projected.${scenario}.${field}`,
        section: "After works",
        label,
        value,
        unit,
        explanation,
      });
    });
  });

  return rows;
}

function buildResultRows(
  simulationType,
  region,
  results,
) {
  if (
    simulationType === "LOAN_CAPACITY" &&
    region === "FLANDERS"
  ) {
    return flandersLoanRows(results);
  }

  if (
    simulationType === "LOAN_CAPACITY" &&
    region === "GIRONA"
  ) {
    return gironaLoanRows(results);
  }

  if (
    simulationType === "MONTHLY_INSTALLMENT" &&
    region === "FLANDERS"
  ) {
    return flandersInstallmentRows(results);
  }

  if (
    simulationType === "MONTHLY_INSTALLMENT" &&
    region === "GIRONA"
  ) {
    return gironaInstallmentRows(results);
  }

  if (
    simulationType === "RENOVATION_COST" &&
    region === "FLANDERS"
  ) {
    return flandersRenovationRows(results);
  }

  if (
    simulationType === "RENOVATION_COST" &&
    region === "GIRONA"
  ) {
    return gironaRenovationRows(results);
  }

  return [];
}

/* =========================================================
   SUMMARY
   ========================================================= */

function interpretationText(
  simulationType,
  region,
) {
  if (
    simulationType === "LOAN_CAPACITY" &&
    region === "FLANDERS"
  ) {
    return (
      "Borrowing capacity is the indicative loan principal supported by the monthly instalment, " +
      "term, interest-rate assumptions and included costs. APRC (TAEG / JKP) expresses the " +
      "overall annualised borrowing cost and is different from the nominal annual interest rate. " +
      "This simulation is guidance only and is not a lender approval or binding offer."
    );
  }

  if (simulationType === "LOAN_CAPACITY") {
    return (
      "This simulation provides an indicative financing capacity based on the borrower, property " +
      "and market assumptions entered. It is not a credit decision or binding loan offer."
    );
  }

  if (simulationType === "MONTHLY_INSTALLMENT") {
    return (
      "This simulation estimates the monthly repayment and the lifetime cost of the loan under " +
      "the selected amount, term and interest-rate assumptions. Actual lender terms may differ."
    );
  }

  return (
    "This simulation provides an indicative renovation-cost estimate. Actual contractor quotations, " +
    "technical design choices and final project costs may differ."
  );
}

function selectSummaryKpis(
  simulationType,
  region,
  resultRows,
) {
  const find = (key) =>
    resultRows.find(
      (row) => row.key === key,
    );

  let keys = [];

  if (
    simulationType === "LOAN_CAPACITY" &&
    region === "FLANDERS"
  ) {
    keys = [
      "borrowingCapacity",
      "instalment",
      "taeg",
      "oneOffCosts",
    ];
  } else if (
    simulationType === "LOAN_CAPACITY" &&
    region === "GIRONA"
  ) {
    keys = [
      "loanAmount",
      "savings",
      "instalmentInitial",
      "tae",
    ];
  } else if (
    simulationType === "MONTHLY_INSTALLMENT" &&
    region === "FLANDERS"
  ) {
    keys = [
      "instalment",
      "loanAmount",
      "totalInterest",
      "totalRepaid",
    ];
  } else if (
    simulationType === "MONTHLY_INSTALLMENT" &&
    region === "GIRONA"
  ) {
    keys = [
      "products.mixed.instalmentInitial",
      "loanAmount",
      "ltv",
      "products.mixed.tae",
    ];
  } else if (
    simulationType === "RENOVATION_COST" &&
    region === "FLANDERS"
  ) {
    keys = [
      "budget.budget.typical",
      "budget.budget.low",
      "budget.budget.high",
      "perMeasure.total.typical",
    ];
  } else if (
    simulationType === "RENOVATION_COST" &&
    region === "GIRONA"
  ) {
    keys = [
      "budget",
      "facadeArea",
      "projected.conservative.saving",
      "projected.standard.saving",
    ];
  }

  const selected =
    keys.map(find).filter(Boolean);

  if (selected.length < 4) {
    for (const row of resultRows) {
      if (
        selected.length >= 4
      ) {
        break;
      }

      if (
        !selected.includes(row) &&
        typeof row.value === "number"
      ) {
        selected.push(row);
      }
    }
  }

  return selected.slice(0, 4);
}

function buildSummarySheet({
  simulationType,
  region,
  generatedAt,
  calculatorVersion,
  resultRows,
}) {
  const worksheet =
    XLSX.utils.aoa_to_sheet([
      [simulationTitle(simulationType)],
      [
        `${regionLabel(region)} · LEG-UP indicative simulation`,
      ],
      [],
      [
        "Generated",
        new Date(
          generatedAt,
        ).toLocaleString(),
        "",
        "Parameter set",
        calculatorVersion || "—",
      ],
      [],
      ["At a glance"],
      [],
      [],
      [],
      ["What this means"],
      [
        interpretationText(
          simulationType,
          region,
        ),
      ],
      [],
      ["Important when editing this workbook"],
      [
        (
          "The green cells in Inputs are editable. Results are a snapshot from the moment the file was downloaded " +
          "and do NOT automatically recalculate when Inputs are changed in Excel. To obtain updated results, upload " +
          "the workbook back to LEG-UP and choose Upload & Apply. LEG-UP will validate the inputs and recalculate."
        ),
      ],
    ]);

  worksheet["!merges"] = [
    XLSX.utils.decode_range("A1:H1"),
    XLSX.utils.decode_range("A2:H2"),
    XLSX.utils.decode_range("A6:H6"),
    XLSX.utils.decode_range("A10:H10"),
    XLSX.utils.decode_range("A11:H11"),
    XLSX.utils.decode_range("A13:H13"),
    XLSX.utils.decode_range("A14:H14"),
  ];

  applyStyleToRange(
    worksheet,
    "A1:H1",
    styles.title,
  );
  applyStyleToRange(
    worksheet,
    "A2:H2",
    styles.subtitle,
  );
  applyStyleToRange(
    worksheet,
    "A6:H6",
    styles.section,
  );
  applyStyleToRange(
    worksheet,
    "A10:H10",
    styles.section,
  );
  applyStyleToRange(
    worksheet,
    "A11:H11",
    styles.note,
  );
  applyStyleToRange(
    worksheet,
    "A13:H13",
    styles.section,
  );
  applyStyleToRange(
    worksheet,
    "A14:H14",
    styles.warning,
  );

  worksheet["A4"].s =
    cloneStyle(styles.header);
  worksheet["B4"].s =
    cloneStyle(styles.body);
  worksheet["D4"].s =
    cloneStyle(styles.header);
  worksheet["E4"].s =
    cloneStyle(styles.body);

  const kpis =
    selectSummaryKpis(
      simulationType,
      region,
      resultRows,
    );

  const starts = [
    ["A7:B7", "A8:B9"],
    ["C7:D7", "C8:D9"],
    ["E7:F7", "E8:F9"],
    ["G7:H7", "G8:H9"],
  ];

  kpis.forEach(
    (row, index) => {
      const [
        labelRange,
        valueRange,
      ] = starts[index];

      worksheet["!merges"].push(
        XLSX.utils.decode_range(
          labelRange,
        ),
        XLSX.utils.decode_range(
          valueRange,
        ),
      );

      const labelCell =
        labelRange.split(":")[0];

      const valueCell =
        valueRange.split(":")[0];

      worksheet[labelCell] = {
        t: "s",
        v: row.label,
      };

      worksheet[valueCell] = {
        t: "s",
        v: displayValue(
          row.value,
          row.unit,
        ),
      };

      applyStyleToRange(
        worksheet,
        labelRange,
        styles.kpiLabel,
      );

      applyStyleToRange(
        worksheet,
        valueRange,
        styles.kpiValue,
      );
    },
  );

  worksheet["!cols"] = [
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
  ];

  /*
   * Rows 2–5 intentionally larger for readability.
   */
  worksheet["!rows"] = [
    { hpt: 34 }, // 1
    { hpt: 34 }, // 2
    { hpt: 16 }, // 3
    { hpt: 30 }, // 4
    { hpt: 16 }, // 5
    { hpt: 24 }, // 6
    { hpt: 24 }, // 7
    { hpt: 32 }, // 8
    { hpt: 32 }, // 9
    { hpt: 24 }, // 10
    { hpt: 58 }, // 11
    { hpt: 14 }, // 12
    { hpt: 24 }, // 13
    { hpt: 64 }, // 14
  ];

  return worksheet;
}

/* =========================================================
   INPUTS SHEET
   ========================================================= */

function buildInputsSheet(
  inputRows,
) {
  const rows = [
    [
      "LEG-UP Simulation Inputs",
      "",
      "",
      "",
      "",
    ],
    [
      (
        "Edit the green Value cells if needed. " +
        "These are the canonical values that LEG-UP will read when the workbook is uploaded."
      ),
      "",
      "",
      "",
      "",
    ],
    [],
    [
      "Key",
      "Field",
      "Value",
      "Unit",
      "Explanation",
    ],
    ...inputRows.map((row) => [
      row.path,
      inputLabel(row.path),
      row.value,
      inputUnit(row.path),
      inputExplanation(row.path),
    ]),
  ];

  const worksheet =
    XLSX.utils.aoa_to_sheet(
      rows,
    );

  worksheet["!merges"] = [
    XLSX.utils.decode_range(
      "A1:E1",
    ),
    XLSX.utils.decode_range(
      "A2:E2",
    ),
  ];

  applyStyleToRange(
    worksheet,
    "A1:E1",
    styles.title,
  );
  applyStyleToRange(
    worksheet,
    "A2:E2",
    styles.note,
  );
  applyStyleToRange(
    worksheet,
    "A4:E4",
    styles.header,
  );

  inputRows.forEach(
    (row, index) => {
      const excelRow =
        index + 5;

      applyStyleToRange(
        worksheet,
        `A${excelRow}:B${excelRow}`,
        styles.body,
      );

      setCellStyle(
        worksheet,
        `C${excelRow}`,
        styles.editable,
        numberFormatFor(
          row.path,
          row.value,
          inputUnit(row.path),
        ),
      );

      applyStyleToRange(
        worksheet,
        `D${excelRow}:E${excelRow}`,
        styles.body,
      );
    },
  );

  worksheet["!cols"] = [
    { wch: 34, hidden: true },
    { wch: 36 },
    { wch: 22 },
    { wch: 16 },
    { wch: 66 },
  ];

  worksheet["!rows"] = [
    { hpt: 32 },
    { hpt: 48 },
    { hpt: 10 },
    { hpt: 26 },
  ];

  worksheet["!autofilter"] = {
    ref:
      `A4:E${
        inputRows.length + 4
      }`,
  };

  return worksheet;
}

/* =========================================================
   RESULTS SHEET
   ========================================================= */

function buildResultsSheet(
  resultRows,
) {
  const rows = [
    [
      "LEG-UP Simulation Results",
      "",
      "",
      "",
      "",
      "",
    ],
    [
      (
        "These are the indicative results calculated when this workbook was downloaded. " +
        "They do not automatically update when the Inputs sheet is edited."
      ),
      "",
      "",
      "",
      "",
      "",
    ],
    [],
    [
      "Key",
      "Section",
      "Result",
      "Value",
      "Unit",
      "What this result means",
    ],
    ...resultRows.map((row) => [
      row.key,
      row.section,
      row.label,
      row.value,
      row.unit,
      row.explanation,
    ]),
  ];

  const worksheet =
    XLSX.utils.aoa_to_sheet(
      rows,
    );

  worksheet["!merges"] = [
    XLSX.utils.decode_range(
      "A1:F1",
    ),
    XLSX.utils.decode_range(
      "A2:F2",
    ),
  ];

  applyStyleToRange(
    worksheet,
    "A1:F1",
    styles.title,
  );
  applyStyleToRange(
    worksheet,
    "A2:F2",
    styles.warning,
  );
  applyStyleToRange(
    worksheet,
    "A4:F4",
    styles.header,
  );

  resultRows.forEach(
    (row, index) => {
      const excelRow =
        index + 5;

      applyStyleToRange(
        worksheet,
        `A${excelRow}:C${excelRow}`,
        styles.body,
      );

      setCellStyle(
        worksheet,
        `D${excelRow}`,
        styles.resultValue,
        numberFormatFor(
          row.key,
          row.value,
          row.unit,
        ),
      );

      applyStyleToRange(
        worksheet,
        `E${excelRow}:F${excelRow}`,
        styles.body,
      );
    },
  );

  worksheet["!cols"] = [
    { wch: 36, hidden: true },
    { wch: 22 },
    { wch: 39 },
    { wch: 22 },
    { wch: 16 },
    { wch: 76 },
  ];

  worksheet["!rows"] = [
    { hpt: 32 },
    { hpt: 50 },
    { hpt: 10 },
    { hpt: 26 },
  ];

  return worksheet;
}

/* =========================================================
   TECHNICAL METADATA
   ========================================================= */

function buildTechnicalSheet({
  exportId,
  generatedAt,
  simulationType,
  region,
  language,
  calculatorVersion,
}) {
  const rows = [
    ["Key", "Value"],
    [
      "export_type",
      LEGUP_EXPORT_TYPE,
    ],
    [
      "schema_version",
      LEGUP_SCHEMA_VERSION,
    ],
    [
      "export_id",
      exportId,
    ],
    [
      "generated_at",
      generatedAt,
    ],
    [
      "simulation_type",
      simulationType,
    ],
    [
      "region",
      region,
    ],
    [
      "language",
      language,
    ],
    [
      "calculator_version",
      calculatorVersion || "",
    ],
    [
      "canonical_input_sheet",
      "Inputs",
    ],
    [
      "input_key_column",
      "A",
    ],
    [
      "input_value_column",
      "C",
    ],
    [
      "results_policy",
      (
        "Results are an exported snapshot only. " +
        "Do not import them as authoritative values."
      ),
    ],
    [
      "import_policy",
      (
        "Read editable values from Inputs by Key, validate them, " +
        "then recalculate all derived results in LEG-UP."
      ),
    ],
  ];

  const worksheet =
    XLSX.utils.aoa_to_sheet(
      rows,
    );

  applyStyleToRange(
    worksheet,
    "A1:B1",
    styles.header,
  );

  for (
    let row = 2;
    row <= rows.length;
    row += 1
  ) {
    applyStyleToRange(
      worksheet,
      `A${row}:B${row}`,
      styles.body,
    );
  }

  worksheet["!cols"] = [
    { wch: 28 },
    { wch: 95 },
  ];

  return worksheet;
}

/* =========================================================
   DOWNLOAD
   ========================================================= */

function normaliseFilePart(value) {
  return String(
    value || "simulation",
  )
    .trim()
    .toLowerCase()
    .replace(
      /[^a-z0-9]+/g,
      "-",
    )
    .replace(
      /^-+|-+$/g,
      "",
    );
}

function downloadWorkbook(
  workbook,
  filename,
) {
  const workbookArray =
    XLSX.write(
      workbook,
      {
        bookType: "xlsx",
        type: "array",
        compression: true,
        cellStyles: true,
      },
    );

  const blob = new Blob(
    [workbookArray],
    {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
  );

  const url =
    window.URL.createObjectURL(
      blob,
    );

  const anchor =
    document.createElement("a");

  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = "none";

  document.body.appendChild(
    anchor,
  );

  anchor.click();
  anchor.remove();

  window.setTimeout(() => {
    window.URL.revokeObjectURL(
      url,
    );
  }, 1000);
}

/* =========================================================
   PUBLIC API
   ========================================================= */

export function downloadSimulationWorkbook({
  simulationType,
  region,
  inputs,
  results,
  language = "en",
  calculatorVersion = "",
}) {
  try {
    if (
      typeof window ===
        "undefined" ||
      typeof document ===
        "undefined"
    ) {
      throw new Error(
        "Excel export is only available in the browser.",
      );
    }

    const generatedAt =
      new Date().toISOString();

    const exportId =
      createExportId();

    const inputRows =
      flattenInputs(
        inputs ?? {},
      );

    /*
     * Results are intentionally curated instead of flattening
     * the complete internal calculator object. Internal arrays
     * such as 300-row amortisation schedules are not exported.
     */
    const resultRows =
      buildResultRows(
        simulationType,
        region,
        results ?? {},
      );

    const workbook =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      buildSummarySheet({
        simulationType,
        region,
        generatedAt,
        calculatorVersion,
        resultRows,
      }),
      "Summary",
    );

    XLSX.utils.book_append_sheet(
      workbook,
      buildInputsSheet(
        inputRows,
      ),
      "Inputs",
    );

    XLSX.utils.book_append_sheet(
      workbook,
      buildResultsSheet(
        resultRows,
      ),
      "Results",
    );

    XLSX.utils.book_append_sheet(
      workbook,
      buildTechnicalSheet({
        exportId,
        generatedAt,
        simulationType,
        region,
        language,
        calculatorVersion,
      }),
      "LEGUP_DATA",
    );

    workbook.Workbook = {
      Sheets: [
        { Hidden: 0 },
        { Hidden: 0 },
        { Hidden: 0 },
        { Hidden: 1 },
      ],
    };

    workbook.Props = {
      Title:
        `LEG-UP ${simulationType}`,
      Subject:
        "LEG-UP public simulation export",
      Author:
        "LEG-UP",
      Company:
        "LEG-UP",
      Comments:
        (
          "LEG-UP simulation export " +
          `schema ${LEGUP_SCHEMA_VERSION}. ` +
          "Inputs is the canonical import sheet; Results is a snapshot."
        ),
      CreatedDate:
        new Date(),
    };

    const datePart =
      generatedAt.slice(
        0,
        10,
      );

    const filename =
      [
        "legup",
        normaliseFilePart(
          region,
        ),
        normaliseFilePart(
          simulationType,
        ),
        datePart,
      ]
        .filter(Boolean)
        .join("_") +
      ".xlsx";

    downloadWorkbook(
      workbook,
      filename,
    );

    return {
      ok: true,
      filename,
      exportId,
      exportedResultCount:
        resultRows.length,
    };
  } catch (error) {
    console.error(
      "[LEG-UP] Could not export simulation workbook:",
      error,
    );

    throw error;
  }
}
